import { useEffect } from "react"
import { useForm, useFieldArray } from "react-hook-form"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import { FiPlus, FiTrash2 } from "react-icons/fi"

import { updateProfile } from "../../../../services/operations/SettingsAPI"
import { apiConnector } from "../../../../services/apiconnector"
import { profileEndpoints } from "../../../../services/apis"
import { setUser } from "../../../../slices/profileSlice"
import IconBtn from "../../../common/IconBtn"

const genders = ["Male", "Female", "Non-Binary", "Prefer not to say", "Other"]

const currentYear = new Date().getFullYear()
const yearRange = { min: 1950, max: currentYear + 6 }

const toValues = (user) => {
  const ad = user?.additionalDetails
  return {
    firstName:     user?.firstName     ?? "",
    lastName:      user?.lastName      ?? "",
    dateOfBirth:   ad?.dateOfBirth     ?? "",
    gender:        ad?.gender          ?? "",
    contactNumber: ad?.contactNumber   ?? "",
    about:         ad?.about           ?? "",
    address:       ad?.address         ?? "",
    jobTitle:      ad?.jobTitle        ?? "",
    company:       ad?.company         ?? "",
    linkedin:      ad?.linkedin        ?? "",
    github:        ad?.github          ?? "",
    instagram:     ad?.instagram       ?? "",
    website:       ad?.website         ?? "",
    education:     ad?.education       ?? [],
  }
}

export default function EditProfile() {
  const { user } = useSelector((state) => state.profile)
  const { token } = useSelector((state) => state.auth)
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { register, handleSubmit, control, reset, formState: { errors } } = useForm({
    defaultValues: toValues(user),
  })

  // Silently fetch fresh user data on mount so additionalDetails is always populated
  useEffect(() => {
    const refresh = async () => {
      try {
        const res = await apiConnector("GET", profileEndpoints.GET_USER_DETAILS_API, null, {
          Authorization: `Bearer ${token}`,
        })
        if (res?.data?.success) {
          const fresh = res.data.data
          const image = fresh.image ||
            `https://api.dicebear.com/5.x/initials/svg?seed=${fresh.firstName} ${fresh.lastName}`
          dispatch(setUser({ ...fresh, image }))
        }
      } catch { /* silent — form will fall back to Redux cache */ }
    }
    if (token) refresh()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  // Re-populate form whenever Redux user data loads or changes
  useEffect(() => {
    if (user) reset(toValues(user))
  }, [user, reset])

  const { fields, append, remove } = useFieldArray({ control, name: "education" })

  const submitProfileForm = async (data) => {
    try {
      dispatch(updateProfile(token, data))
    } catch (error) {
      console.log("ERROR MESSAGE - ", error.message)
    }
  }

  const addEducation = () =>
    append({ collegeName: "", university: "", courseName: "", startYear: "", passingYear: "", grade: "" })

  return (
    <form onSubmit={handleSubmit(submitProfileForm)}>

      {/* ── Profile Information ─────────────────────────────────── */}
      <div className="my-10 flex flex-col gap-y-6 rounded-md border border-richblack-700 bg-richblack-800 p-8 px-12">
        <h2 className="text-lg font-semibold text-richblack-5">Profile Information</h2>

        <div className="flex flex-col gap-5 lg:flex-row">
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">First Name</label>
            <input type="text" placeholder="Enter first name" className="form-style"
              {...register("firstName", { required: true })} />
            {errors.firstName && <span className="-mt-1 text-[12px] text-yellow-100">Please enter your first name.</span>}
          </div>
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">Last Name</label>
            <input type="text" placeholder="Enter last name" className="form-style"
              {...register("lastName", { required: true })} />
            {errors.lastName && <span className="-mt-1 text-[12px] text-yellow-100">Please enter your last name.</span>}
          </div>
        </div>

        <div className="flex flex-col gap-5 lg:flex-row">
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">Date of Birth</label>
            <input type="date" className="form-style"
              {...register("dateOfBirth", {
                max: { value: new Date().toISOString().split("T")[0], message: "Date of Birth cannot be in the future." },
              })} />
            {errors.dateOfBirth && <span className="-mt-1 text-[12px] text-yellow-100">{errors.dateOfBirth.message}</span>}
          </div>
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">Gender</label>
            <select className="form-style" {...register("gender")}>
              <option value="">Select gender</option>
              {genders.map((g, i) => <option key={i} value={g}>{g}</option>)}
            </select>
          </div>
        </div>

        <div className="flex flex-col gap-5 lg:flex-row">
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">Contact Number</label>
            <input type="tel" placeholder="Enter contact number" className="form-style"
              {...register("contactNumber", {
                minLength: { value: 10, message: "Invalid contact number" },
                maxLength: { value: 12, message: "Invalid contact number" },
              })} />
            {errors.contactNumber && <span className="-mt-1 text-[12px] text-yellow-100">{errors.contactNumber.message}</span>}
          </div>
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">About</label>
            <input type="text" placeholder="Short bio…" className="form-style" {...register("about")} />
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <label className="lable-style">Address</label>
          <textarea rows={2} placeholder="City, State, Country" className="form-style resize-none"
            {...register("address")} />
        </div>
      </div>

      {/* ── Education ───────────────────────────────────────────── */}
      <div className="my-10 flex flex-col gap-y-6 rounded-md border border-richblack-700 bg-richblack-800 p-8 px-12">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-richblack-5">Education</h2>
          <button
            type="button"
            onClick={addEducation}
            className="flex items-center gap-1.5 rounded-lg bg-richblack-700 px-3 py-1.5 text-xs font-medium text-richblack-200 transition-all hover:bg-richblack-600 hover:text-richblack-5"
          >
            <FiPlus size={13} /> Add Education
          </button>
        </div>

        {fields.length === 0 && (
          <p className="text-sm italic text-richblack-500">
            No education added yet. Click "Add Education" to add your degree, diploma, or certification.
          </p>
        )}

        {fields.map((field, index) => (
          <div key={field.id} className="relative rounded-xl border border-richblack-600 bg-richblack-700/50 p-5">
            {/* Remove button */}
            <button
              type="button"
              onClick={() => remove(index)}
              className="absolute right-3 top-3 rounded-lg p-1.5 text-richblack-400 transition-colors hover:bg-richblack-600 hover:text-pink-300"
              title="Remove"
            >
              <FiTrash2 size={14} />
            </button>

            <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-richblack-400">
              Education {index + 1}
            </p>

            <div className="flex flex-col gap-4">
              {/* Row 1: College name + University */}
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex flex-col gap-2 sm:w-1/2">
                  <label className="lable-style">College / Institution <span className="text-pink-200">*</span></label>
                  <input
                    type="text"
                    placeholder="e.g. IIT Bombay"
                    className="form-style"
                    {...register(`education.${index}.collegeName`, { required: "College name is required" })}
                  />
                  {errors.education?.[index]?.collegeName && (
                    <span className="-mt-1 text-[12px] text-yellow-100">{errors.education[index].collegeName.message}</span>
                  )}
                </div>
                <div className="flex flex-col gap-2 sm:w-1/2">
                  <label className="lable-style">University / Board</label>
                  <input
                    type="text"
                    placeholder="e.g. Mumbai University"
                    className="form-style"
                    {...register(`education.${index}.university`)}
                  />
                </div>
              </div>

              {/* Row 2: Course / Degree + Grade */}
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex flex-col gap-2 sm:w-1/2">
                  <label className="lable-style">Degree / Course</label>
                  <input
                    type="text"
                    placeholder="e.g. B.Tech Computer Science"
                    className="form-style"
                    {...register(`education.${index}.courseName`)}
                  />
                </div>
                <div className="flex flex-col gap-2 sm:w-1/2">
                  <label className="lable-style">Grade / CGPA / Percentage</label>
                  <input
                    type="text"
                    placeholder="e.g. 8.5 CGPA or 85%"
                    className="form-style"
                    {...register(`education.${index}.grade`)}
                  />
                </div>
              </div>

              {/* Row 3: Start year + Passing year */}
              <div className="flex flex-col gap-4 sm:flex-row">
                <div className="flex flex-col gap-2 sm:w-1/2">
                  <label className="lable-style">Start Year</label>
                  <input
                    type="number"
                    placeholder="e.g. 2019"
                    className="form-style"
                    {...register(`education.${index}.startYear`, {
                      min: { value: yearRange.min, message: `Min year is ${yearRange.min}` },
                      max: { value: yearRange.max, message: `Max year is ${yearRange.max}` },
                    })}
                  />
                  {errors.education?.[index]?.startYear && (
                    <span className="-mt-1 text-[12px] text-yellow-100">{errors.education[index].startYear.message}</span>
                  )}
                </div>
                <div className="flex flex-col gap-2 sm:w-1/2">
                  <label className="lable-style">Passing Year</label>
                  <input
                    type="number"
                    placeholder="e.g. 2023 (or leave blank if ongoing)"
                    className="form-style"
                    {...register(`education.${index}.passingYear`, {
                      min: { value: yearRange.min, message: `Min year is ${yearRange.min}` },
                      max: { value: yearRange.max, message: `Max year is ${yearRange.max}` },
                    })}
                  />
                  {errors.education?.[index]?.passingYear && (
                    <span className="-mt-1 text-[12px] text-yellow-100">{errors.education[index].passingYear.message}</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}

        {fields.length > 0 && (
          <button
            type="button"
            onClick={addEducation}
            className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-richblack-600 py-3 text-sm text-richblack-400 transition-colors hover:border-richblack-500 hover:text-richblack-200"
          >
            <FiPlus size={15} /> Add Another
          </button>
        )}
      </div>

      {/* ── Professional ────────────────────────────────────────── */}
      <div className="my-10 flex flex-col gap-y-6 rounded-md border border-richblack-700 bg-richblack-800 p-8 px-12">
        <h2 className="text-lg font-semibold text-richblack-5">Professional</h2>

        <div className="flex flex-col gap-5 lg:flex-row">
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">Job Title / Role</label>
            <input type="text" placeholder="e.g. Full Stack Developer" className="form-style" {...register("jobTitle")} />
          </div>
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">Company / Organisation</label>
            <input type="text" placeholder="e.g. Google, Self-employed" className="form-style" {...register("company")} />
          </div>
        </div>
      </div>

      {/* ── Social Links ────────────────────────────────────────── */}
      <div className="my-10 flex flex-col gap-y-6 rounded-md border border-richblack-700 bg-richblack-800 p-8 px-12">
        <h2 className="text-lg font-semibold text-richblack-5">Social Links</h2>

        <div className="flex flex-col gap-5 lg:flex-row">
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">LinkedIn</label>
            <input type="url" placeholder="https://linkedin.com/in/username" className="form-style"
              {...register("linkedin", { pattern: { value: /^https?:\/\/.+/, message: "Enter a valid URL (https://…)" } })} />
            {errors.linkedin && <span className="-mt-1 text-[12px] text-yellow-100">{errors.linkedin.message}</span>}
          </div>
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">GitHub</label>
            <input type="url" placeholder="https://github.com/username" className="form-style"
              {...register("github", { pattern: { value: /^https?:\/\/.+/, message: "Enter a valid URL (https://…)" } })} />
            {errors.github && <span className="-mt-1 text-[12px] text-yellow-100">{errors.github.message}</span>}
          </div>
        </div>

        <div className="flex flex-col gap-5 lg:flex-row">
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">Instagram</label>
            <input type="url" placeholder="https://instagram.com/username" className="form-style"
              {...register("instagram", { pattern: { value: /^https?:\/\/.+/, message: "Enter a valid URL (https://…)" } })} />
            {errors.instagram && <span className="-mt-1 text-[12px] text-yellow-100">{errors.instagram.message}</span>}
          </div>
          <div className="flex flex-col gap-2 lg:w-[48%]">
            <label className="lable-style">Personal Website</label>
            <input type="url" placeholder="https://yourwebsite.com" className="form-style"
              {...register("website", { pattern: { value: /^https?:\/\/.+/, message: "Enter a valid URL (https://…)" } })} />
            {errors.website && <span className="-mt-1 text-[12px] text-yellow-100">{errors.website.message}</span>}
          </div>
        </div>
      </div>

      {/* ── Actions ─────────────────────────────────────────────── */}
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate("/dashboard/my-profile")}
          className="cursor-pointer rounded-md bg-richblack-700 py-2 px-5 font-semibold text-richblack-50"
        >
          Cancel
        </button>
        <IconBtn type="submit" text="Save" />
      </div>
    </form>
  )
}
